import React from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import logger from "@/utils/logger";

interface StatItem {
  value: string;
  label: string;
  image?: string;
  icon?: string;
}

const defaultIconSrcs = [
  "/assets/img/icon/counter_1_1.svg",
  "/assets/img/icon/counter_1_2.svg",
  "/assets/img/icon/counter_1_3.svg",
  "/assets/img/icon/counter_1_4.svg",
];

const getStatIconSrc = (stat: StatItem, idx: number) => {
  if (stat.image) return stat.image;
  if (stat.icon === "package") return "/assets/img/icon/counter_1_1.svg";
  if (stat.icon === "bag") return "/assets/img/icon/counter_1_2.svg";
  if (stat.icon === "clock") return "/assets/img/icon/counter_1_3.svg";
  if (stat.icon === "users") return "/assets/img/icon/counter_1_4.svg";
  return defaultIconSrcs[idx % defaultIconSrcs.length];
};

export default function HokStats({
  content,
  onContentChange,
  isSelected,
}: SectionContentProps) {
  const primaryColor = content.colorPalette?.primary || "#684DF4";
  const secondaryColor = content.colorPalette?.secondary || "#8a2be2";
  const fontFamily = content.fontFamily || "'Inter', sans-serif";

  const defaultStats: StatItem[] = [
    { value: "259k", label: "TOTAL ITEMS", image: "/assets/img/icon/counter_1_1.svg" },
    { value: "152k", label: "SOLD ITEMS", image: "/assets/img/icon/counter_1_2.svg" },
    { value: "580", label: "PENDING ITEMS", image: "/assets/img/icon/counter_1_3.svg" },
    { value: "39k+", label: "HAPPY CLIENTS", image: "/assets/img/icon/counter_1_4.svg" },
  ];

  const stats: StatItem[] = content.stats || defaultStats;

  const updateStat = (idx: number, field: keyof StatItem, value: string) => {
    if (!onContentChange) return;
    const newStats = [...stats];
    newStats[idx] = { ...newStats[idx], [field]: value };
    onContentChange({ ...content, stats: newStats });
  };

  const addStat = () => {
    if (!onContentChange) return;
    const newStats = [...stats, {
      value: "0",
      label: "NEW STAT",
      image: defaultIconSrcs[stats.length % defaultIconSrcs.length]
    }];
    onContentChange({ ...content, stats: newStats });
  };

  const removeStat = (idx: number) => {
    if (!onContentChange) return;
    const newStats = stats.filter((_, i) => i !== idx);
    onContentChange({ ...content, stats: newStats });
  };

  return (
    <div
      className={`relative overflow-hidden w-full pt-10 pb-10 ${isSelected ? "ring-2 ring-blue-500 relative z-20" : ""}`}
      style={{
        fontFamily,
        background: "url('/assets/img/bg/testi_bg_1.png') no-repeat center / cover, linear-gradient(180deg, #F1F4FD 0%, #F9FAFD 100%)",
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 relative z-10">
        {/* Section Header */}
        <div className="flex justify-center mb-6 sm:mb-8">
          <div className="text-center max-w-2xl">
            <span
              className="inline-block text-xs sm:text-sm font-bold tracking-wider uppercase mb-3 text-[#684DF4]"
              style={{ color: primaryColor }}
            >
              <EditableText
                value={content.subtitle || "Our Achievements"}
                onChange={(val) => onContentChange?.({ ...content, subtitle: val })}
                as="span"
                placeholder="Subtitle"
              />
            </span>
            <h2 className="text-3xl sm:text-3xl font-bold text-gray-900 leading-tight tracking-tight">
              <EditableText
                value={content.title || "Our Numbers Speak For Us"}
                onChange={(val) => onContentChange?.({ ...content, title: val })}
                as="span"
                placeholder="Title"
              />
            </h2>
          </div>
        </div>

        {/* Top row: Stats + Graphic Image */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          {/* Stats grid */}
          <div className="lg:col-span-7">
            <div className="grid grid-cols-2 gap-2">
              {stats.map((stat, idx) => {
                return (
                  <div
                    key={idx}
                    className="group relative flex flex-col items-center text-center p-6 sm:p-8 rounded-[24px] bg-white shadow-[0_10px_35px_rgba(0,0,0,0.03)] hover:shadow-md hover:-translate-y-1 transition-all duration-300 cursor-pointer min-h-[220px]"
                  >
                    {isSelected && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeStat(idx);
                        }}
                        className="absolute top-3 right-3 h-7 w-7 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-sm font-bold z-20"
                        title="Remove"
                      >
                        ×
                      </button>
                    )}

                    {/* Icon container */}
                    <div className="relative w-16 h-16 rounded-full bg-[#EBF5FF] flex items-center justify-center mb-5 transition-transform group-hover:scale-105">
                      <img
                        src={getStatIconSrc(stat, idx)}
                        alt={stat.label}
                        className="w-8 h-8 object-contain pointer-events-none"
                      />
                      {isSelected && (
                        <label className="absolute inset-0 z-10 flex items-center justify-center bg-black/60 rounded-full hover:bg-black/80 cursor-pointer transition-all opacity-0 group-hover:opacity-100 font-bold text-xs text-white">
                          Upload
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onClick={(e) => e.stopPropagation()}
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                const toast = (await import("react-hot-toast")).default;
                                const homepageApi = (await import("@/services/homepageApi")).default;
                                const toastId = toast.loading("Uploading icon...");
                                try {
                                  const response = await homepageApi.uploadImage(file);
                                  const url = response.data?.url || response.url;
                                  if (url) {
                                    updateStat(idx, "image", url);
                                    toast.success("Icon updated!", { id: toastId });
                                  } else {
                                    toast.error("Failed to upload.", { id: toastId });
                                  }
                                } catch (err) {
                                  toast.error("Upload failed", { id: toastId });
                                  logger.error(err);
                                }
                              }
                            }}
                          />
                        </label>
                      )}
                    </div>

                    {/* Value */}
                    <h3 className="text-[40px] font-semibold text-gray-900 leading-none tracking-tight">
                      <EditableText
                        value={stat.value}
                        onChange={(val) => updateStat(idx, "value", val)}
                        as="span"
                        placeholder="0"
                      />
                    </h3>

                    {/* Label */}
                    <p className="text-xs text-[#7D849A] font-normal tracking-widest uppercase mt-3">
                      <EditableText
                        value={stat.label}
                        onChange={(val) => updateStat(idx, "label", val)}
                        as="span"
                        placeholder="Label"
                      />
                    </p>
                  </div>
                );
              })}

              {isSelected && (
                <button
                  onClick={addStat}
                  className="flex flex-col items-center justify-center min-h-[220px] rounded-[24px] border-2 border-dashed border-gray-200 bg-gray-50 text-gray-400 hover:bg-gray-100 hover:border-gray-300 hover:text-gray-600 transition-all"
                >
                  <span className="text-3xl mb-1 opacity-50">+</span>
                  <span className="text-xs font-semibold opacity-70">Add Stat</span>
                </button>
              )}
            </div>
          </div>

          {/* Right: Graphic Image */}
          <div className="lg:col-span-5 flex justify-center lg:justify-end select-none">
            <img
              src="/assets/img/normal/cta-img.png"
              alt="Achievements"
              className="w-full max-w-sm h-auto object-contain pointer-events-none"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
